import React, { useEffect, useState } from 'react';
import { Calendar, GasPump as Fuel, Gear as Cog, Gauge } from '@phosphor-icons/react';
import { useDutyFree } from '../../context/DutyFreeContext';
import { useTranslation } from '../../i18n/I18nContext';
import { useShowcaseText } from '../../i18n/useShowcaseText';
import { useTranslateVehicleValue } from '../../utils/vehicleValues';
import { generateImgproxyUrl } from '../../services/imgproxy';
import NewModelLanding from './NewModelLanding';
import SpecCard from './SpecCard';
import BrandLogoPill from './BrandLogoPill';
import { formatRegYear } from '../../utils/registrationYear';
import { pickHeroImageSource } from './utils/imageSource';
import { formatMileage } from './utils/formatMileage';

const FALLBACK_VALUE = '—';

/**
 * Visual body of a car landing (issue #965). Extracted from CarLandingPage
 * so the showroom flow can render two of them — one as the active card
 * (interactive, with chrome) and one as a peek behind the active card
 * during a swipe. Owns its own image-resolve state per car instance so
 * the peek can preload independently of the front card.
 *
 * Dispatches between new-model (Autodis OMODA/JAECOO, issue #884) and
 * used-VIN layouts.
 *
 * @param {object} props
 * @param {object} props.car — DealerCarDto.
 * @param {boolean} [props.isInteractive=true] — when false (peek mode),
 *   the CTA button is rendered inert (aria-hidden + tabIndex=-1).
 * @param {() => void} [props.onOpenWidget] — CTA handler; ignored when
 *   isInteractive is false.
 */
const HERO_IMG_WIDTH = 800;
const HERO_IMG_HEIGHT = 600;

const formatPrice = (price, contactLabel = 'Contact') => {
  if (!price || price === 0) return contactLabel;
  return `€${price.toLocaleString()}`;
};

const LandingBody = ({ car, isInteractive = true, onOpenWidget }) => {
  const { isDutyFree, calculateDutyFree } = useDutyFree();
  const { t, translateInventory } = useTranslation();
  const showcaseText = useShowcaseText();
  // Топливо и коробка на языке покупателя — только на витрине (#1441), как в
  // каталоге (#1417). У дилеров значение показывается как пришло, без
  // словаря: так было до перевода.
  const translateVehicleValue = useTranslateVehicleValue();
  const valueLabel = (value, field) =>
    value && translateInventory ? translateVehicleValue(value, field) : value;
  const [mainImage, setMainImage] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);

  const cleanMake = car?.make?.replace(/\*/g, '') || car?.make || '';
  const cleanModel = car?.model?.replace(/\*/g, '') || car?.model || '';
  const displayTitle = car?.name || `${cleanMake} ${cleanModel}`.trim();
  const displayPrice = car && isDutyFree ? calculateDutyFree(car) : car?.price;
  const sourceImagePath = pickHeroImageSource(car);

  useEffect(() => {
    setMainImage(null);
    setImgFailed(false);
    if (!sourceImagePath) return undefined;
    let isMounted = true;
    generateImgproxyUrl(sourceImagePath, HERO_IMG_WIDTH, HERO_IMG_HEIGHT).then((url) => {
      if (isMounted) setMainImage(url);
    });
    return () => { isMounted = false; };
  }, [sourceImagePath]);

  if (!car) return null;

  if (car.isNewModel) {
    return <NewModelLanding car={car} title={displayTitle} />;
  }

  const imageUrl = mainImage || sourceImagePath;
  const showPlaceholder = !imageUrl || imgFailed;

  // Снимок ещё раз — размытым фоном под всей карточкой (витрина, #1441).
  // Только переменная: дилерский `CarLandingPage.css` её не читает.
  const photoBackdrop = showPlaceholder ? undefined : { '--landing-photo': `url("${imageUrl}")` };

  return (
    <div className="landing landing--in-stack">
      <div className="landing__container" style={photoBackdrop}>
        <div className="landing__photo-wrapper">
          {!showPlaceholder && (
            <img
              className="landing__photo"
              src={imageUrl}
              alt={displayTitle}
              onError={() => setImgFailed(true)}
              style={car.imagePosition ? { objectPosition: car.imagePosition } : undefined}
            />
          )}
          {showPlaceholder && (
            <div className="landing__photo-placeholder">
              <span className="landing__photo-placeholder-text">No photo available</span>
            </div>
          )}
          <BrandLogoPill car={car} />
          {displayPrice > 0 && (
            <div className="landing__price-badge">
              <span className="landing__price-text">{formatPrice(displayPrice, showcaseText('showcase.car.contactPrice', 'Contact'))}</span>
              <span className="landing__price-reg">{showcaseText('showcase.car.reg', '+reg')}</span>
            </div>
          )}
        </div>

        <div className="landing__card">
          <div className="landing__card-header">
            <h1 className="landing__title">{displayTitle}</h1>
          </div>

          <div className="landing__specs-grid">
            {/* Год возвращён для used-VIN (поддержанные авто, Andys): #1021
                убрал его, но тот запрос был про new-model (Autodis), где
                свежий год выглядит как б/у. У б/у машин год — важная инфа. (#1064) */}
            <SpecCard icon={Calendar} value={formatRegYear(car, FALLBACK_VALUE)} />
            <SpecCard icon={Gauge} value={formatMileage(car.mileage, translateInventory ? car.condition : undefined)} />
            <SpecCard icon={Fuel} value={valueLabel(car.fuelType, 'fuelType') || FALLBACK_VALUE} />
            <SpecCard
              icon={Cog}
              value={valueLabel(car.transmission, 'transmission') || FALLBACK_VALUE}
            />
          </div>

          <button
            type="button"
            className="landing__cta"
            onClick={isInteractive ? onOpenWidget : undefined}
            tabIndex={isInteractive ? 0 : -1}
            aria-hidden={!isInteractive || undefined}
            aria-label={t('landing.cta.learnMore')}
          >
            {t('landing.cta.learnMore')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LandingBody);
